/**
 * GPS Data Manager
 * Global singleton for managing multiple GPS Data Providers with centralized control
 * Now uses direct callbacks instead of subscriptions
 */

import { GpsDataProvider } from './GpsDataProvider';
import type { 
  GpsDataProviderConfig,
  GpsManagerDataCallback,
  GpsManagerStatsCallback,
  GpsManagerProviderCallback
} from './types';
import type { DeviceGpsInfo } from '../gps-websocket/types';

/**
 * GPS Data Manager configuration interface
 */
export interface GpsDataManagerConfig {
  // Provider limits
  maxProviders: number;
  
  // Memory management
  maxHistorySize: number;
  cleanupInterval: number;
  
  // Performance monitoring
  enablePerformanceMonitoring: boolean;
  statsUpdateInterval: number;
}

/**
 * Global GPS statistics interface
 */
export interface GlobalGpsStats {
  totalProviders: number;
  totalDevices: number;
  totalUpdatesPerMinute: number;
  memoryUsage: {
    providers: number;
    cachedData: number;
    callbacks: number;
  };
  performance: {
    averageProcessingTime: number;
    peakProcessingTime: number;
    totalProcessedUpdates: number;
  };
}

/**
 * Default GPS Data Manager configuration
 */
const DEFAULT_GPS_MANAGER_CONFIG: GpsDataManagerConfig = {
  maxProviders: 10,
  maxHistorySize: 1000,
  cleanupInterval: 60000,      // 1분
  enablePerformanceMonitoring: true,
  statsUpdateInterval: 5000    // 5초
};

/**
 * GPS Data Manager Class
 * Singleton pattern for managing multiple GPS data providers globally
 * Uses direct callbacks instead of subscription pattern
 */
export class GpsDataManager {
  private static instance: GpsDataManager | null = null;
  
  // Configuration and state
  private config: GpsDataManagerConfig;
  private isInitialized: boolean = false;
  
  // Provider management
  private providers: Map<string, GpsDataProvider> = new Map();
  private providerConfigs: Map<string, GpsDataProviderConfig> = new Map();
  
  // Direct callback system
  private dataCallbacks: Set<GpsManagerDataCallback> = new Set();
  private statsCallbacks: Set<GpsManagerStatsCallback> = new Set();
  private providerAddedCallbacks: Set<GpsManagerProviderCallback> = new Set();
  private providerRemovedCallbacks: Set<GpsManagerProviderCallback> = new Set();
  
  // Statistics and monitoring
  private globalStats: GlobalGpsStats;
  private statsTimer: NodeJS.Timeout | null = null;
  private cleanupTimer: NodeJS.Timeout | null = null;
  
  // Data aggregation
  private aggregatedData: Map<string, DeviceGpsInfo[]> = new Map();

  /**
   * Private constructor for singleton pattern
   * @param config - Manager configuration
   */
  private constructor(config: Partial<GpsDataManagerConfig> = {}) {
    this.config = { ...DEFAULT_GPS_MANAGER_CONFIG, ...config };
    
    // Initialize global statistics
    this.globalStats = {
      totalProviders: 0,
      totalDevices: 0,
      totalUpdatesPerMinute: 0,
      memoryUsage: {
        providers: 0,
        cachedData: 0,
        callbacks: 0
      },
      performance: {
        averageProcessingTime: 0,
        peakProcessingTime: 0,
        totalProcessedUpdates: 0
      }
    };
    
    this.startPerformanceMonitoring();
    this.startCleanupRoutine();
  }

  /**
   * Get singleton instance
   * @param config - Optional configuration for first initialization
   * @returns GPS Data Manager instance
   */
  static getInstance(config?: Partial<GpsDataManagerConfig>): GpsDataManager {
    if (!GpsDataManager.instance) {
      GpsDataManager.instance = new GpsDataManager(config);
    }
    return GpsDataManager.instance;
  }

  /**
   * Reset singleton instance (for testing)
   */
  static resetInstance(): void {
    if (GpsDataManager.instance) {
      GpsDataManager.instance.destroy();
      GpsDataManager.instance = null;
    }
  }

  /**
   * Initialize the manager
   */
  initialize(): void {
    if (this.isInitialized) {
      console.warn('GPS Data Manager already initialized');
      return;
    }
    
    this.isInitialized = true;
    console.log('✅ GPS Data Manager initialized');
  }

  /**
   * Create a new GPS data provider
   * @param id - Unique provider ID
   * @param config - Provider configuration
   * @returns Created GPS data provider
   */
  createProvider(id: string, config: Partial<GpsDataProviderConfig>): GpsDataProvider {
    if (this.providers.has(id)) {
      throw new Error(`Provider with ID '${id}' already exists`);
    }
    
    if (this.providers.size >= this.config.maxProviders) {
      throw new Error(`Maximum number of providers (${this.config.maxProviders}) reached`);
    }
    
    // Merge with default configuration
    const fullConfig: GpsDataProviderConfig = {
      enableCaching: true,
      cacheSize: 100,
      autoFilter: {
        excludeInactive: false,
        excludeInvalidCoords: true,
        excludeStatic: false,
        minSpeedThreshold: 0.5
      },
      maxDevicesPerUpdate: 50,
      enableBatching: true,
      batchInterval: 2000,
      updateThreshold: {
        position: 0.0001,
        speed: 1,
        interval: 1000
      },
      ...config
    } as GpsDataProviderConfig;
    
    // Create provider
    const provider = new GpsDataProvider(fullConfig);
    
    // Setup provider callback handlers for global tracking
    this.setupProviderCallbacks(id, provider);
    
    // Store provider and config
    this.providers.set(id, provider);
    this.providerConfigs.set(id, fullConfig);
    
    // Update global stats
    this.updateGlobalStats();
    
    // Notify provider added callbacks
    this.notifyProviderAdded(id, provider);
    
    console.log(`🏭 Created GPS Data Provider: ${id}`);
    return provider;
  }

  /**
   * Get existing GPS data provider
   * @param id - Provider ID
   * @returns GPS data provider or null if not found
   */
  getProvider(id: string): GpsDataProvider | null {
    return this.providers.get(id) || null;
  }

  /**
   * Get all provider IDs
   * @returns Array of provider IDs
   */
  getProviderIds(): string[] {
    return Array.from(this.providers.keys());
  }

  /**
   * Remove GPS data provider
   * @param id - Provider ID to remove
   * @returns True if provider was removed
   */
  removeProvider(id: string): boolean {
    const provider = this.providers.get(id);
    if (!provider) {
      return false;
    }
    
    // Clear provider callbacks
    provider.clearAllCallbacks();
    
    // Destroy provider
    provider.destroy();
    
    // Remove from maps
    this.providers.delete(id);
    this.providerConfigs.delete(id);
    this.aggregatedData.delete(id);
    
    // Update global stats
    this.updateGlobalStats();
    
    // Notify provider removed callbacks
    this.notifyProviderRemoved(id);
    
    console.log(`🗑️ Removed GPS Data Provider: ${id}`);
    return true;
  }

  /**
   * Register callback for global GPS data updates
   * @param callback - Function to call when global data updates
   */
  registerDataCallback(callback: GpsManagerDataCallback): void {
    this.dataCallbacks.add(callback);
    
    // Send current aggregated data immediately
    if (this.aggregatedData.size > 0) {
      try {
        callback(new Map(this.aggregatedData));
      } catch (error) {
        console.error('Error in immediate data callback:', error);
      }
    }
  }

  /**
   * Unregister global data callback
   * @param callback - Callback to remove
   */
  unregisterDataCallback(callback: GpsManagerDataCallback): void {
    this.dataCallbacks.delete(callback);
  }

  /**
   * Register callback for global statistics updates
   * @param callback - Function to call when stats update
   */
  registerStatsCallback(callback: GpsManagerStatsCallback): void {
    this.statsCallbacks.add(callback);
    
    // Send current stats immediately
    try {
      callback(this.globalStats);
    } catch (error) {
      console.error('Error in immediate stats callback:', error);
    }
  }

  /**
   * Unregister global stats callback
   * @param callback - Callback to remove
   */
  unregisterStatsCallback(callback: GpsManagerStatsCallback): void {
    this.statsCallbacks.delete(callback);
  }

  /**
   * Register callback for provider added events
   * @param callback - Function to call when provider is added
   */
  registerProviderAddedCallback(callback: GpsManagerProviderCallback): void {
    this.providerAddedCallbacks.add(callback);
  }

  /**
   * Unregister provider added callback
   * @param callback - Callback to remove
   */
  unregisterProviderAddedCallback(callback: GpsManagerProviderCallback): void {
    this.providerAddedCallbacks.delete(callback);
  }

  /**
   * Register callback for provider removed events
   * @param callback - Function to call when provider is removed
   */
  registerProviderRemovedCallback(callback: GpsManagerProviderCallback): void {
    this.providerRemovedCallbacks.add(callback);
  }

  /**
   * Unregister provider removed callback
   * @param callback - Callback to remove
   */
  unregisterProviderRemovedCallback(callback: GpsManagerProviderCallback): void {
    this.providerRemovedCallbacks.delete(callback);
  }

  /**
   * Clear all callbacks
   */
  clearAllCallbacks(): void {
    this.dataCallbacks.clear();
    this.statsCallbacks.clear();
    this.providerAddedCallbacks.clear();
    this.providerRemovedCallbacks.clear();
    console.log('🧹 All manager callbacks cleared');
  }

  /**
   * Get global statistics
   * @returns Global GPS statistics
   */
  getGlobalStats(): GlobalGpsStats {
    return { ...this.globalStats };
  }

  /**
   * Get aggregated data from all providers
   * @returns Map of provider ID to device data
   */
  getAggregatedData(): Map<string, DeviceGpsInfo[]> {
    return new Map(this.aggregatedData);
  }

  /**
   * Get combined data from all providers
   * @returns Combined device data from all providers
   */
  getCombinedData(): DeviceGpsInfo[] {
    const combined: DeviceGpsInfo[] = [];
    
    this.aggregatedData.forEach((devices) => {
      combined.push(...devices);
    });
    
    // Remove duplicates by PSN (keep latest)
    const uniqueDevices = new Map<string, DeviceGpsInfo>();
    combined.forEach(device => {
      uniqueDevices.set(device.psn, device);
    });
    
    return Array.from(uniqueDevices.values());
  }

  /**
   * Update manager configuration
   * @param newConfig - New configuration
   */
  updateConfig(newConfig: Partial<GpsDataManagerConfig>): void {
    this.config = { ...this.config, ...newConfig };
    
    // Restart monitoring if settings changed
    if (newConfig.enablePerformanceMonitoring !== undefined) {
      if (newConfig.enablePerformanceMonitoring) {
        this.startPerformanceMonitoring();
      } else {
        this.stopPerformanceMonitoring();
      }
    }
    
    console.log('🔧 GPS Data Manager configuration updated');
  }

  /**
   * Destroy manager and all providers
   */
  destroy(): void {
    // Stop timers
    this.stopPerformanceMonitoring();
    this.stopCleanupRoutine();
    
    // Destroy all providers
    this.providers.forEach((provider, id) => {
      provider.clearAllCallbacks();
      provider.destroy();
      console.log(`🗑️ Destroyed provider: ${id}`);
    });
    
    // Clear all data
    this.providers.clear();
    this.providerConfigs.clear();
    this.aggregatedData.clear();
    
    // Clear all callbacks
    this.clearAllCallbacks();
    
    this.isInitialized = false;
    console.log('🗑️ GPS Data Manager destroyed');
  }

  /**
   * Setup callback handlers for a provider
   * @param providerId - Provider ID
   * @param provider - Provider instance
   */
  private setupProviderCallbacks(providerId: string, provider: GpsDataProvider): void {
    // Register callback for data updates
    provider.registerUpdateCallback((devices: DeviceGpsInfo[]) => {
      this.aggregatedData.set(providerId, devices);
      this.updateGlobalStats();
      this.notifyDataCallbacks();
    });
    
    // Register callback for provider errors
    provider.registerErrorCallback((error: Error) => {
      console.error(`❌ Provider ${providerId} error:`, error);
    });
  }

  /**
   * Update global statistics
   */
  private updateGlobalStats(): void {
    this.globalStats.totalProviders = this.providers.size;
    
    // Calculate total devices and updates per minute
    let totalDevices = 0;
    let totalUpdatesPerMinute = 0;
    
    this.providers.forEach((provider) => {
      const state = provider.getState();
      totalDevices += state.deviceCount;
      totalUpdatesPerMinute += state.stats.updatesPerMinute;
    });
    
    this.globalStats.totalDevices = totalDevices;
    this.globalStats.totalUpdatesPerMinute = totalUpdatesPerMinute;
    
    // Update memory usage (approximate)
    this.globalStats.memoryUsage = {
      providers: this.providers.size * 1000, // Approximate bytes per provider
      cachedData: this.aggregatedData.size * totalDevices * 200, // Approximate bytes per device
      callbacks: (this.dataCallbacks.size + this.statsCallbacks.size) * 100 // Approximate bytes per callback
    };
  }

  /**
   * Notify data callbacks of global data updates
   */
  private notifyDataCallbacks(): void {
    const allData = new Map(this.aggregatedData);
    
    this.dataCallbacks.forEach((callback) => {
      try {
        callback(allData);
      } catch (error) {
        console.error('Error notifying global data callback:', error);
      }
    });
  }

  /**
   * Notify stats callbacks of statistics updates
   */
  private notifyStatsCallbacks(): void {
    this.statsCallbacks.forEach((callback) => {
      try {
        callback(this.globalStats);
      } catch (error) {
        console.error('Error notifying global stats callback:', error);
      }
    });
  }

  /**
   * Notify provider added callbacks
   * @param providerId - Provider ID
   * @param provider - Provider instance
   */
  private notifyProviderAdded(providerId: string, provider: GpsDataProvider): void {
    this.providerAddedCallbacks.forEach((callback) => {
      try {
        callback(providerId, provider);
      } catch (error) {
        console.error('Error notifying provider added callback:', error);
      }
    });
  }

  /**
   * Notify provider removed callbacks
   * @param providerId - Provider ID
   */
  private notifyProviderRemoved(providerId: string): void {
    this.providerRemovedCallbacks.forEach((callback) => {
      try {
        callback(providerId);
      } catch (error) {
        console.error('Error notifying provider removed callback:', error);
      }
    });
  }

  /**
   * Start performance monitoring
   */
  private startPerformanceMonitoring(): void {
    if (!this.config.enablePerformanceMonitoring || this.statsTimer) {
      return;
    }
    
    this.statsTimer = setInterval(() => {
      this.updateGlobalStats();
      this.notifyStatsCallbacks();
    }, this.config.statsUpdateInterval);
  }

  /**
   * Stop performance monitoring
   */
  private stopPerformanceMonitoring(): void {
    if (this.statsTimer) {
      clearInterval(this.statsTimer);
      this.statsTimer = null;
    }
  }

  /**
   * Start cleanup routine
   */
  private startCleanupRoutine(): void {
    this.cleanupTimer = setInterval(() => {
      this.performCleanup();
    }, this.config.cleanupInterval);
  }

  /**
   * Stop cleanup routine
   */
  private stopCleanupRoutine(): void {
    if (this.cleanupTimer) {
      clearInterval(this.cleanupTimer);
      this.cleanupTimer = null;
    }
  }

  /**
   * Perform cleanup operations
   */
  private performCleanup(): void {
    // Clean up old aggregated data
    if (this.aggregatedData.size > this.config.maxHistorySize) {
      const keysToRemove = Array.from(this.aggregatedData.keys()).slice(0, 
        this.aggregatedData.size - this.config.maxHistorySize
      );
      
      keysToRemove.forEach(key => {
        this.aggregatedData.delete(key);
      });
    }
    
    console.log('🧹 GPS Data Manager cleanup completed');
  }
}

/**
 * Create or get GPS Data Manager instance
 * @param config - Optional configuration
 * @returns GPS Data Manager instance
 */
export function createGpsDataManager(config?: Partial<GpsDataManagerConfig>): GpsDataManager {
  return GpsDataManager.getInstance(config);
}

/**
 * Get existing GPS Data Manager instance
 * @returns GPS Data Manager instance or null if not created
 */
export function getGpsDataManager(): GpsDataManager | null {
  try {
    return GpsDataManager.getInstance();
  } catch {
    return null;
  }
}